import { useEffect, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useActivity } from "@/context/ActivityContext";
import Modal from "@/components/Modal";
import Pagination from "@/components/Pagination";
import { Truck, Plus, Send, PackageCheck, Undo2, Loader2, UserCog } from "lucide-react";
import { PackEquiv } from "@/lib/units";

const STATUS = {
  prepared: ["Préparé", "bg-[#FEF3C7] text-[#B45309]"],
  sent: ["Envoyé", "bg-[#DBEAFE] text-[#1D4ED8]"],
  partial: ["Partiellement reçu", "bg-[#FEF9C3] text-[#A16207]"],
  received: ["Reçu", "bg-[#DCFCE7] text-[#15803D]"],
};
const RET_STATUS = { in_transit: ["En transit", "bg-[#DBEAFE] text-[#1D4ED8]"], received: ["Reçu", "bg-[#DCFCE7] text-[#15803D]"] };
const round4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;

export default function Supplies() {
  const { user } = useAuth();
  const { current } = useActivity();
  const [rows, setRows] = useState([]);
  const [posRets, setPosRets] = useState([]);
  const [retPage, setRetPage] = useState(1);
  const [prRecv, setPrRecv] = useState(null);
  const [prRecvLines, setPrRecvLines] = useState({});
  const [prRecvReason, setPrRecvReason] = useState("");
  const [products, setProducts] = useState([]);
  const [locs, setLocs] = useState([]);
  const [receiverOpts, setReceiverOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ to_location_id: "" });
  const [sendTarget, setSendTarget] = useState(null);
  const [sendReceiver, setSendReceiver] = useState("");
  const [reassignTarget, setReassignTarget] = useState(null);
  const [reassignReceiver, setReassignReceiver] = useState("");
  const [qtys, setQtys] = useState({});
  const [checked, setChecked] = useState({});
  const [supCat, setSupCat] = useState("");
  const [bulkQty, setBulkQty] = useState("");
  const [onlyStock, setOnlyStock] = useState(false);
  const [pkgs, setPkgs] = useState({});
  const [recv, setRecv] = useState(null);
  const [recvLines, setRecvLines] = useState({});
  const [ret, setRet] = useState(false);
  const [retForm, setRetForm] = useState({ location_id: "", lines: {}, reason: "" });
  const [busy, setBusy] = useState(false);
  const canManage = ["owner", "admin", "manager", "storekeeper"].includes(user.role);
  const canReassign = ["owner", "admin", "manager"].includes(user.role);
  const [page, setPage] = useState(1);
  const [cats, setCats] = useState([]);
  const [allLocs, setAllLocs] = useState([]);

  const load = async () => {
    if (!current) return;
    const [s, p, l, c, pr] = await Promise.all([
      api.get(`/supplies?activity_id=${current.id}`).catch(() => ({ data: [] })),
      api.get(`/products?activity_id=${current.id}`).catch(() => ({ data: [] })),
      api.get(`/locations?activity_id=${current.id}`).catch(() => ({ data: [] })),
      api.get(`/categories?activity_id=${current.id}`).catch(() => ({ data: [] })),
      api.get(`/pos-returns?activity_id=${current.id}`).catch(() => ({ data: [] })),
    ]);
    setRows(s.data); setPage(1); setPosRets(pr.data); setRetPage(1); setProducts(p.data);
    setLocs(l.data.filter((x) => x.type === "pos")); setAllLocs(l.data); setCats(c.data);
  };
  useEffect(() => { load(); }, [current]);

  if (!current) return <div className="text-[#64748B]">Sélectionnez une activité.</div>;

  const prod = (id) => products.find((p) => p.id === id);
  const srcLoc = allLocs.find((l) => l.id === form.from_location_id) || null;
  const srcStock = (p) => (form.from_location_id && p ? (p.stock_by_location?.[form.from_location_id] ?? 0) : (p?.stock ?? 0));
  const pkgFactor = (p, key) => (key && key !== "base" && p ? (p.packagings?.find((x) => x.name === key)?.factor || 1) : 1);
  const remainingOf = (l) => Math.max(round4((l.sent_qty || 0) - (l.received_qty || 0) - (l.lost_qty || 0)), 0);

  // ---- Création : par catégorie ----
  const defaultSource = () => {
    const whs = allLocs.filter((x) => x.type === "warehouse");
    const withStock = whs.find((w) => products.some((pr) => ((pr.stock_by_location || {})[w.id] || 0) > 0));
    return (withStock || whs[0] || allLocs[0] || {}).id || "";
  };
  const openNew = () => { setForm({ from_location_id: defaultSource(), to_location_id: "" }); setQtys({}); setChecked({}); setSupCat(""); setBulkQty(""); setOpen(true); };
  const baseCatProducts = supCat ? products.filter((p) => p.category_id === supCat) : [];
  const catProducts = onlyStock ? baseCatProducts.filter((p) => srcStock(p) > 0) : baseCatProducts;
  const draftLines = Object.entries(qtys).filter(([, q]) => Number(q) > 0);
  const overCount = draftLines.filter(([pid, q]) => Number(q) > srcStock(prod(pid)) + 1e-6).length;
  const applyBulk = () => { if (bulkQty === "") return; setQtys((q) => { const n = { ...q }; Object.keys(checked).forEach((pid) => { if (checked[pid]) n[pid] = bulkQty; }); return n; }); };
  const toggleAllCat = (on) => setChecked((c) => { const n = { ...c }; catProducts.forEach((p) => { n[p.id] = on; }); return n; });

  const create = async () => {
    if (!form.from_location_id) return toast.error("Point émetteur obligatoire");
    if (!form.to_location_id) return toast.error("Point de vente destination obligatoire");
    if (form.from_location_id === form.to_location_id) return toast.error("Le point émetteur et la destination doivent être différents");
    const lines = Object.entries(qtys).filter(([, q]) => Number(q) > 0).map(([product_id, q]) => ({ product_id, sent_qty: Number(q) * pkgFactor(prod(product_id), pkgs[product_id]) }));
    if (!lines.length) return toast.error("Saisissez une quantité pour au moins un produit");
    const over = lines.filter((l) => Number(l.sent_qty) > srcStock(prod(l.product_id)) + 1e-6);
    if (over.length) return toast.error(`${over.length} produit(s) dépassent le stock disponible au point émetteur`);
    setBusy(true);
    try { await api.post("/supplies", { activity_id: current.id, from_location_id: form.from_location_id, to_location_id: form.to_location_id, lines }); toast.success("Approvisionnement préparé"); setOpen(false); setForm({ from_location_id: "", to_location_id: "" }); setQtys({}); setChecked({}); setSupCat(""); setBulkQty(""); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  // ---- Envoi : choix du réceptionnaire désigné (utilisateur autorisé du PDV destination) ----
  const roleLabel = (r) => ({ cashier: "Caissier / Vendeur", seller: "Caissier / Vendeur", storekeeper: "Magasinier", manager: "Responsable", admin: "Administrateur", owner: "Propriétaire" }[r] || r || "");
  const loadReceivers = async (posId) => {
    const r = await api.get(`/supplies/eligible-receivers?activity_id=${current.id}&location_id=${posId}`);
    return r.data || [];
  };
  const openSend = async (s) => {
    setSendTarget(s); setSendReceiver(""); setReceiverOpts([]);
    try {
      const opts = await loadReceivers(s.to_location_id);
      setReceiverOpts(opts);
      const cash = opts.find((o) => (o.role === "cashier" || o.role === "seller") && !o.is_self);
      const self = opts.find((o) => o.is_self);
      setSendReceiver(cash ? cash.id : (self ? self.id : (opts[0]?.id || "")));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const doSend = async () => {
    if (!sendReceiver) return toast.error("Choisissez un réceptionnaire");
    setBusy(true);
    try { await api.post(`/supplies/${sendTarget.id}/send`, { receiver_id: sendReceiver }); toast.success("Envoyé — quantités en transit"); setSendTarget(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const openReassign = async (s) => {
    setReassignTarget(s); setReassignReceiver(s.receiver_id || ""); setReceiverOpts([]);
    try { setReceiverOpts(await loadReceivers(s.to_location_id)); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const doReassign = async () => {
    if (!reassignReceiver) return toast.error("Choisissez un réceptionnaire");
    setBusy(true);
    try { await api.post(`/supplies/${reassignTarget.id}/reassign-receiver`, { receiver_id: reassignReceiver }); toast.success("Réceptionnaire réattribué"); setReassignTarget(null); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  // ---- Réception : tous les produits expédiés, écart par produit ----
  const openRecv = (s) => {
    setRecv(s);
    const init = {};
    s.lines.forEach((l) => { const rem = remainingOf(l); if (rem > 1e-6) init[l.product_id] = { received: rem, ecart_type: "transit", reason: "" }; });
    setRecvLines(init);
  };
  const doReceive = async () => {
    const entries = Object.entries(recvLines);
    for (const [pid, v] of entries) {
      const l = recv.lines.find((x) => x.product_id === pid); const rem = remainingOf(l);
      const rec = Number(v.received || 0); const ec = round4(rem - rec);
      if (rec > rem + 1e-6) return toast.error(`Reçu supérieur au reste en transit pour ${l.product_name}`);
      if (ec > 1e-6 && (rec > 1e-6 || v.ecart_type === "loss") && !(v.reason || "").trim()) return toast.error(`Motif requis pour l'écart sur ${l.product_name}`);
    }
    const anyAction = entries.some(([pid, v]) => { const l = recv.lines.find((x) => x.product_id === pid); const rem = remainingOf(l); const rec = Number(v.received || 0); return rec > 1e-6 || (v.ecart_type === "loss" && round4(rem - rec) > 1e-6); });
    if (!anyAction) return toast.error("Saisissez au moins une quantité reçue ou une perte");
    setBusy(true);
    try {
      await api.post(`/supplies/${recv.id}/receive`, { lines: entries.map(([product_id, v]) => ({ product_id, received_qty: Number(v.received || 0), ecart_type: v.ecart_type || "transit", reason: v.reason || null })) });
      toast.success("Réception confirmée"); setRecv(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  // ---- Retours PDV -> entrepôt ----
  const doReturn = async () => {
    const lines = Object.entries(retForm.lines).filter(([, q]) => Number(q) > 0).map(([product_id, q]) => ({ product_id, quantity: Number(q) }));
    if (!retForm.location_id || !lines.length) return toast.error("Point de vente et quantités obligatoires");
    setBusy(true);
    try { await api.post("/pos-returns", { activity_id: current.id, location_id: retForm.location_id, lines, reason: retForm.reason || null }); toast.success("Retour initié — stock en transit vers l'entrepôt"); setRet(false); setRetForm({ location_id: "", lines: {}, reason: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const openPrRecv = (r) => { setPrRecv(r); setPrRecvLines(Object.fromEntries(r.lines.map((l) => [l.product_id, l.quantity]))); setPrRecvReason(""); };
  const doPrReceive = async () => {
    setBusy(true);
    try {
      await api.post(`/pos-returns/${prRecv.id}/receive`, { lines: prRecv.lines.map((l) => ({ product_id: l.product_id, received_qty: Number(prRecvLines[l.product_id] ?? 0) })), discrepancy_reason: prRecvReason || null });
      toast.success("Réception confirmée à l'entrepôt"); setPrRecv(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Approvisionnements</h1>
          <p className="text-sm text-[#64748B] mt-1">Point émetteur (entrepôt ou PDV) → point de vente destination. Le réceptionnaire confirme la réception. Activité : {current.name}</p></div>
        <div className="flex gap-2">
          <button onClick={() => setRet(true)} className="btn-outline" data-testid="pos-return-btn"><Undo2 className="w-4 h-4" /> Retour vers entrepôt</button>
          {canManage && <button onClick={openNew} className="btn-primary" data-testid="new-supply-btn"><Plus className="w-4 h-4" /> Nouvel approvisionnement</button>}
        </div>
      </div>

      <div className="space-y-3">
        {rows.slice((page - 1) * 4, page * 4).map((s) => (
          <div key={s.id} className="card p-4" data-testid={`supply-card-${s.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#0E7490]/10 text-[#0E7490] flex items-center justify-center"><Truck className="w-4 h-4" /></div>
                <div>
                  <div className="font-semibold text-[#16324F]">{s.from_location_name} → {s.to_location_name}{s.ref ? ` · ${s.ref}` : ""}</div>
                  <div className="text-xs text-[#64748B]">{s.sender_name ? `Expédié par ${s.sender_name}` : `Préparé par ${s.prepared_by_name}`}{s.receiver_name ? ` → réception : ${s.receiver_name}` : ""} · {fmtDate(s.sent_at || s.created_at)}</div>
                </div>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS[s.status]?.[1]}`} data-testid={`supply-status-${s.id}`}>{STATUS[s.status]?.[0]}</span>
            </div>
            <div className="mt-3 text-sm text-[#334155]">
              {s.lines.map((l) => {
                const rem = remainingOf(l);
                return (
                  <div key={l.product_id} className="flex flex-wrap justify-between gap-x-2 border-b border-[#F1F5F9] py-1">
                    <span>{l.product_name}</span>
                    <span className="text-right">
                      envoyé <b>{l.sent_qty}</b>
                      {(s.status === "partial" || s.status === "received") && <> · reçu <b>{l.received_qty || 0}</b></>}
                      {l.lost_qty > 0 && <span className="text-[#B91C1C]"> · perte {l.lost_qty}</span>}
                      {(s.status === "sent" || s.status === "partial") && rem > 0 && <span className="text-[#1D4ED8]"> · reste {rem} en transit</span>}
                      <PackEquiv p={prod(l.product_id)} qty={l.sent_qty} />
                    </span>
                  </div>
                );
              })}
            </div>
            {(s.receptions || []).length > 0 && (
              <div className="mt-2 space-y-1" data-testid={`supply-receptions-${s.id}`}>
                {s.receptions.map((rc) => (
                  <div key={rc.ref} className="rounded-lg bg-[#F8FAFC] border border-[#EEF2F7] p-2 text-xs text-[#475569]">
                    <div className="font-semibold text-[#334155]">Réception {rc.ref} · {fmtDate(rc.at)} · {rc.received_by_name}</div>
                    {rc.lines.map((rl, i) => (
                      <div key={i} className="flex flex-wrap justify-between gap-x-2">
                        <span>{rl.product_name}</span>
                        <span>reçu <b>{rl.received_qty}</b>{rl.ecart > 0 && <span className="text-[#B45309]"> · écart {rl.ecart} ({rl.ecart_type === "loss" ? "perte" : "reste en transit"}{rl.reason ? ` — ${rl.reason}` : ""})</span>}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {(s.receiver_history || []).length > 0 && (
              <div className="mt-2 text-[11px] text-[#64748B]" data-testid={`supply-reassign-history-${s.id}`}>
                {s.receiver_history.map((h, i) => (
                  <div key={i}>Réattribué {h.from_name ? `de ${h.from_name} ` : ""}à <b>{h.to_name}</b> par {h.by_name} · {fmtDate(h.at)}</div>
                ))}
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              {s.status === "prepared" && canManage && <button onClick={() => openSend(s)} className="btn-navy !py-1.5 !px-3" data-testid={`send-supply-${s.id}`}><Send className="w-3.5 h-3.5" /> Envoyer</button>}
              {(s.status === "sent" || s.status === "partial") && s.receiver_id === user.id && <button onClick={() => openRecv(s)} className="btn-success !py-1.5 !px-3" data-testid={`receive-supply-${s.id}`}><PackageCheck className="w-3.5 h-3.5" /> Confirmer la réception</button>}
              {(s.status === "sent" || s.status === "partial") && canReassign && <button onClick={() => openReassign(s)} className="btn-outline !py-1.5 !px-3" data-testid={`reassign-supply-${s.id}`}><UserCog className="w-3.5 h-3.5" /> Réattribuer</button>}
              {(s.status === "sent" || s.status === "partial") && s.receiver_id !== user.id && <span className="text-xs text-[#1D4ED8] self-center" data-testid={`supply-transit-${s.id}`}>En transit · réception par {s.receiver_name || "—"}</span>}
              {(s.status === "sent" || s.status === "partial") && s.receiver_id === user.id && <span className="text-xs text-[#1D4ED8] self-center">En transit</span>}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="card p-10 text-center text-[#94A3B8]">Aucun approvisionnement</div>}
      </div>
      <Pagination page={page} setPage={setPage} total={rows.length} perPage={4} />

      <div className="pt-2">
        <h2 className="text-lg font-semibold font-head text-[#16324F]">Retours vers l'entrepôt</h2>
        <p className="text-sm text-[#64748B] mt-0.5 mb-3">Le vendeur initie (stock en transit) ; propriétaire, responsable ou magasinier confirme la réception à l'entrepôt.</p>
        <div className="space-y-3">
          {posRets.slice((retPage - 1) * 6, retPage * 6).map((r) => (
            <div key={r.id} className="card p-4" data-testid={`posret-card-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-[#B45309]/10 text-[#B45309] flex items-center justify-center"><Undo2 className="w-4 h-4" /></div>
                  <div>
                    <div className="font-semibold text-[#16324F]">{r.location_name} → entrepôt</div>
                    <div className="text-xs text-[#64748B]">Initié par {r.sent_by_name || r.seller_name} · {fmtDate(r.created_at)}{r.received_by_name ? ` · reçu par ${r.received_by_name}` : ""}</div>
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${RET_STATUS[r.status]?.[1]}`} data-testid={`posret-status-${r.id}`}>{RET_STATUS[r.status]?.[0]}</span>
              </div>
              <div className="mt-3 text-sm text-[#334155]">
                {r.lines.map((l) => (
                  <div key={l.product_id} className="flex justify-between border-b border-[#F1F5F9] py-1">
                    <span>{l.product_name}</span>
                    <span>envoyé <b>{l.quantity}</b>{r.status === "received" ? <> · reçu <b>{l.received_qty}</b>{l.discrepancy ? <span className="text-[#B91C1C]"> (écart {l.discrepancy})</span> : null}</> : null}<PackEquiv p={prod(l.product_id)} qty={l.quantity} /></span>
                  </div>
                ))}
              </div>
              {r.reason && <p className="text-xs text-[#64748B] mt-1">Motif : {r.reason}</p>}
              {r.discrepancy_reason && <p className="text-xs text-[#B45309] mt-1">Motif écart : {r.discrepancy_reason}</p>}
              <div className="mt-3 flex gap-2">
                {r.status === "in_transit" && canManage && <button onClick={() => openPrRecv(r)} className="btn-success !py-1.5 !px-3" data-testid={`receive-posret-${r.id}`}><PackageCheck className="w-3.5 h-3.5" /> Confirmer la réception</button>}
                {r.status === "in_transit" && <span className="text-xs text-[#1D4ED8] self-center">En transit vers l'entrepôt</span>}
              </div>
            </div>
          ))}
          {posRets.length === 0 && <div className="card p-10 text-center text-[#94A3B8]">Aucun retour vers l'entrepôt</div>}
        </div>
        <Pagination page={retPage} setPage={setRetPage} total={posRets.length} perPage={6} />
      </div>

      {/* ---- Nouvel approvisionnement (par catégorie) ---- */}
      <Modal open={open} onClose={() => setOpen(false)} title="Nouvel approvisionnement" wide testId="supply-modal"
        footer={<><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={create} disabled={busy || draftLines.length === 0} data-testid="save-supply-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Préparer ({draftLines.length})</button></>}>
        <div className="space-y-4">
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="lbl">Point émetteur</label>
                <select className="inp" value={form.from_location_id || ""} onChange={(e) => setForm({ ...form, from_location_id: e.target.value, to_location_id: form.to_location_id === e.target.value ? "" : form.to_location_id })} data-testid="supply-source-select">
                  <option value="">Sélectionner…</option>
                  {allLocs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type === "warehouse" ? " (entrepôt)" : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="lbl">Point de vente destination</label>
                <select className="inp" value={form.to_location_id} onChange={(e) => setForm({ ...form, to_location_id: e.target.value })} data-testid="supply-pos-select">
                  <option value="">Sélectionner…</option>{locs.filter((l) => l.id !== form.from_location_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            </div>
            <p className="text-[11px] text-[#94A3B8] mt-1">Le stock est débité du point émetteur ; le réceptionnaire sera désigné au moment de l'envoi.</p>
          </div>
          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <label className="lbl !mb-0">Produits par catégorie</label>
              <select className="inp !w-auto !py-1.5" value={supCat} onChange={(e) => setSupCat(e.target.value)} data-testid="supply-cat-select">
                <option value="">Choisir une catégorie…</option>
                {cats.map((c) => { const cp = products.filter((p) => p.category_id === c.id); const u = cp.reduce((s, p) => s + srcStock(p), 0); return <option key={c.id} value={c.id}>{`${c.name} — ${cp.length} produits · ${u} au point émetteur`}</option>; })}
              </select>
            </div>
            {supCat ? (
              <>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <button onClick={() => toggleAllCat(true)} className="btn-outline !py-1 text-xs" data-testid="supply-check-all">Tout cocher</button>
                  <button onClick={() => toggleAllCat(false)} className="btn-outline !py-1 text-xs">Tout décocher</button>
                  <div className="flex items-center gap-1 ml-auto">
                    <input type="number" min="0" className="inp !w-24 !py-1.5" placeholder="Qté" value={bulkQty} onChange={(e) => setBulkQty(e.target.value)} data-testid="supply-bulk-qty" />
                    <button onClick={applyBulk} className="btn-navy !py-1.5 !px-3 text-xs" data-testid="supply-apply-bulk">Appliquer aux cochés</button>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 mb-2 text-xs">
                  <span className="text-[#64748B]" data-testid="supply-wh-summary">{srcLoc?.name || "Source"} : <b>{baseCatProducts.filter((p) => srcStock(p) > 0).length}</b>/{baseCatProducts.length} produit(s) en stock</span>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer font-medium text-[#334155]"><input type="checkbox" className="w-3.5 h-3.5 accent-[#2563EB]" checked={onlyStock} onChange={(e) => setOnlyStock(e.target.checked)} data-testid="supply-only-stock" /> En stock au point émetteur uniquement</label>
                </div>
                <div className="max-h-[42vh] overflow-y-auto divide-y divide-[#F1F5F9] border border-[#E7ECF3] rounded-xl">
                  {catProducts.map((p) => {
                    const ws = srcStock(p); const fac = pkgFactor(p, pkgs[p.id]); const q = Number(qtys[p.id] || 0); const base = q * fac; const over = base > ws + 1e-6;
                    return (
                      <div key={p.id} className="flex items-center gap-2 p-2" data-testid={`supply-prod-${p.id}`}>
                        <input type="checkbox" checked={!!checked[p.id]} onChange={(e) => setChecked({ ...checked, [p.id]: e.target.checked })} className="w-4 h-4 accent-[#2563EB] shrink-0" data-testid={`supply-check-${p.id}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#0F172A] truncate">{p.name}</div>
                          <div className="text-[11px] text-[#94A3B8]">Réf {p.barcode || "—"} · <span className={ws <= 0 ? "text-[#B91C1C] font-semibold" : ""}>source {ws} {p.base_unit}</span>{fac !== 1 && q > 0 ? ` · = ${base} ${p.base_unit}` : ""}<PackEquiv p={p} qty={ws} /></div>
                        </div>
                        {p.packagings?.length ? <select className="inp !w-28 !py-1.5 shrink-0" value={pkgs[p.id] || "base"} onChange={(e) => setPkgs({ ...pkgs, [p.id]: e.target.value })} data-testid={`supply-pkg-${p.id}`}><option value="base">{p.base_unit}</option>{p.packagings.map((pk) => <option key={pk.name} value={pk.name}>{pk.name} (×{pk.factor})</option>)}</select> : null}
                        <input type="number" min="0" step="0.001" className={`inp !w-20 !py-1.5 text-right shrink-0 ${over ? "!border-[#FCA5A5] !bg-[#FEF2F2]" : ""}`} placeholder="0" value={qtys[p.id] || ""} onChange={(e) => setQtys({ ...qtys, [p.id]: e.target.value })} data-testid={`supply-qty-${p.id}`} />
                      </div>
                    );
                  })}
                  {catProducts.length === 0 && <div className="p-4 text-center text-sm text-[#94A3B8]">Aucun produit dans cette catégorie</div>}
                </div>
              </>
            ) : <p className="text-xs text-[#94A3B8]">Sélectionnez une catégorie pour afficher ses produits et saisir les quantités. Les quantités sont conservées quand vous changez de catégorie.</p>}
            <div className="rounded-xl bg-[#F8FAFC] border border-[#E7ECF3] p-3 text-sm mt-2" data-testid="supply-recap">
              <b>{draftLines.length}</b> produit(s) à préparer{overCount > 0 && <span className="text-[#B91C1C]"> · {overCount} dépasse(nt) le stock entrepôt</span>}
            </div>
          </div>
        </div>
      </Modal>

      {/* ---- Confirmer la réception : tous les produits expédiés ---- */}
      <Modal open={!!recv} onClose={() => setRecv(null)} title="Confirmer la réception" wide testId="supply-receive-modal"
        footer={<><button className="btn-outline" onClick={() => setRecv(null)}>Annuler</button><button className="btn-success" onClick={doReceive} disabled={busy} data-testid="confirm-receive-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Valider la réception</button></>}>
        <div className="space-y-3">
          <p className="text-sm text-[#64748B]">Saisissez la quantité réellement reçue pour chaque produit. En cas d'écart, indiquez s'il reste en transit ou s'il s'agit d'une perte, avec un motif obligatoire.</p>
          {recv?.lines.map((l) => {
            const rem = remainingOf(l);
            if (rem <= 1e-6) return (
              <div key={l.product_id} className="rounded-xl border border-[#EEF2F7] bg-[#F8FAFC] p-3 text-sm text-[#64748B]" data-testid={`recv-line-${l.product_id}`}>
                <b className="text-[#334155]">{l.product_name}</b> · entièrement traité (reçu {l.received_qty || 0}{l.lost_qty > 0 ? `, perte ${l.lost_qty}` : ""})
              </div>
            );
            const v = recvLines[l.product_id] || { received: rem, ecart_type: "transit", reason: "" };
            const rec = Number(v.received || 0); const ec = round4(rem - rec);
            return (
              <div key={l.product_id} className="rounded-xl border border-[#E7ECF3] p-3" data-testid={`recv-line-${l.product_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium text-[#0F172A]">{l.product_name}
                    <span className="text-[#94A3B8] font-normal"> · expédié {l.sent_qty}{l.received_qty > 0 ? ` · déjà reçu ${l.received_qty}` : ""} · reste {rem} en transit</span>
                    <PackEquiv p={prod(l.product_id)} qty={rem} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#64748B]">Reçu</span>
                    <input type="number" min="0" step="0.001" className="inp !w-24 !py-1.5 text-right" value={v.received} onChange={(e) => setRecvLines({ ...recvLines, [l.product_id]: { ...v, received: e.target.value } })} data-testid={`recv-qty-${l.product_id}`} />
                  </div>
                </div>
                {ec > 1e-6 && (
                  <div className="mt-2 space-y-2 rounded-lg bg-[#FFFBEB] border border-[#FDE68A] p-2.5" data-testid={`recv-ecart-${l.product_id}`}>
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-[#B45309] font-medium">Écart : {ec}</span>
                      <div className="inline-flex rounded-lg border border-[#E7ECF3] overflow-hidden">
                        <button onClick={() => setRecvLines({ ...recvLines, [l.product_id]: { ...v, ecart_type: "transit" } })} className={`px-3 py-1 text-xs ${v.ecart_type !== "loss" ? "bg-[#1D4ED8] text-white" : "bg-white text-[#334155]"}`} data-testid={`recv-ecart-transit-${l.product_id}`}>Reste en transit</button>
                        <button onClick={() => setRecvLines({ ...recvLines, [l.product_id]: { ...v, ecart_type: "loss" } })} className={`px-3 py-1 text-xs ${v.ecart_type === "loss" ? "bg-[#B91C1C] text-white" : "bg-white text-[#334155]"}`} data-testid={`recv-ecart-loss-${l.product_id}`}>Perte / casse</button>
                      </div>
                    </div>
                    {(rec > 1e-6 || v.ecart_type === "loss") && (
                      <input className="inp !py-1.5" placeholder="Motif de l'écart (obligatoire)" value={v.reason} onChange={(e) => setRecvLines({ ...recvLines, [l.product_id]: { ...v, reason: e.target.value } })} data-testid={`recv-reason-${l.product_id}`} />
                    )}
                    <p className="text-[11px] text-[#92400E]">{v.ecart_type === "loss" ? "L'écart sort définitivement du stock (perte/casse)." : "L'écart reste en transit et pourra être réceptionné plus tard."}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal>

      {/* ---- Retour PDV -> entrepôt ---- */}
      <Modal open={ret} onClose={() => setRet(false)} title="Retour point de vente → entrepôt" testId="pos-return-modal"
        footer={<><button className="btn-outline" onClick={() => setRet(false)}>Annuler</button><button className="btn-primary" onClick={doReturn} disabled={busy} data-testid="confirm-return-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer</button></>}>
        <div className="space-y-3">
          <div><label className="lbl">Point de vente</label>
            <select className="inp" value={retForm.location_id} onChange={(e) => setRetForm({ ...retForm, location_id: e.target.value })} data-testid="return-pos-select">
              <option value="">Sélectionner…</option>{locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select></div>
          {retForm.location_id && products.filter((p) => (p.stock_by_location?.[retForm.location_id] ?? 0) > 0).map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3">
              <div className="text-sm">{p.name} <span className="text-[#94A3B8]">· PDV {p.stock_by_location?.[retForm.location_id] ?? 0}</span></div>
              <input type="number" className="inp !w-24 text-right" placeholder="0" value={retForm.lines[p.id] || ""} onChange={(e) => setRetForm({ ...retForm, lines: { ...retForm.lines, [p.id]: e.target.value } })} data-testid={`return-qty-${p.id}`} />
            </div>
          ))}
          {retForm.location_id && products.filter((p) => (p.stock_by_location?.[retForm.location_id] ?? 0) > 0).length === 0 && <p className="text-sm text-[#94A3B8]" data-testid="return-empty">Aucun stock à retourner pour ce point de vente.</p>}
          <div><label className="lbl">Motif</label><input className="inp" value={retForm.reason} onChange={(e) => setRetForm({ ...retForm, reason: e.target.value })} placeholder="Ex : invendu, surstock" /></div>
        </div>
      </Modal>

      <Modal open={!!prRecv} onClose={() => setPrRecv(null)} title="Confirmer la réception à l'entrepôt" testId="posret-receive-modal"
        footer={<><button className="btn-outline" onClick={() => setPrRecv(null)}>Annuler</button><button className="btn-success" onClick={doPrReceive} disabled={busy} data-testid="confirm-posret-receive-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmer</button></>}>
        <div className="space-y-3">
          <p className="text-sm text-[#64748B]">Saisissez la quantité réellement reçue à l'entrepôt. Tout écart (perte/litige) doit être justifié ; seul le reçu crédite l'entrepôt.</p>
          {prRecv?.lines.map((l) => (
            <div key={l.product_id} className="flex items-center justify-between gap-3">
              <div className="text-sm">{l.product_name} <span className="text-[#94A3B8]">· envoyé {l.quantity}</span></div>
              <input type="number" min="0" step="0.001" className="inp !w-28 text-right" value={prRecvLines[l.product_id] ?? 0} onChange={(e) => setPrRecvLines({ ...prRecvLines, [l.product_id]: e.target.value })} data-testid={`posret-recv-qty-${l.product_id}`} />
            </div>
          ))}
          <div><label className="lbl">Motif de l'écart (si écart)</label><input className="inp" value={prRecvReason} onChange={(e) => setPrRecvReason(e.target.value)} data-testid="posret-recv-reason-input" placeholder="Ex : casse au transport, litige" /></div>
        </div>
      </Modal>
      <Modal open={!!sendTarget} onClose={() => setSendTarget(null)} title="Envoyer l'approvisionnement" testId="supply-send-modal"
        footer={<><button className="btn-outline" onClick={() => setSendTarget(null)}>Annuler</button><button className="btn-navy" onClick={doSend} disabled={busy || !sendReceiver} data-testid="confirm-send-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer l'approvisionnement</button></>}>
        {sendTarget && (
          <div className="space-y-3">
            <div className="rounded-xl bg-[#F8FAFC] border border-[#E7ECF3] p-3 text-sm">
              <div className="font-semibold text-[#16324F]">{sendTarget.from_location_name} → {sendTarget.to_location_name} · {sendTarget.ref}</div>
              <div className="text-xs text-[#64748B]">Expéditeur : {user.name} · {sendTarget.lines.length} produit(s)</div>
            </div>
            <div>
              <label className="lbl">Réceptionnaire désigné</label>
              <select className="inp" value={sendReceiver} onChange={(e) => setSendReceiver(e.target.value)} data-testid="send-receiver-select">
                <option value="">Sélectionner…</option>
                {receiverOpts.map((o) => <option key={o.id} value={o.id}>{o.name}{o.is_self ? " (moi)" : ` · ${roleLabel(o.role)}`}</option>)}
              </select>
              <p className="text-[11px] text-[#94A3B8] mt-1">Choisissez le Caissier / Vendeur du point de vente qui confirmera la réception, ou désignez-vous vous-même. Seul le réceptionnaire désigné pourra confirmer.</p>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!reassignTarget} onClose={() => setReassignTarget(null)} title="Réattribuer le réceptionnaire" testId="supply-reassign-modal"
        footer={<><button className="btn-outline" onClick={() => setReassignTarget(null)}>Annuler</button><button className="btn-primary" onClick={doReassign} disabled={busy || !reassignReceiver} data-testid="confirm-reassign-btn">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Réattribuer</button></>}>
        {reassignTarget && (
          <div className="space-y-3">
            <div className="rounded-xl bg-[#F8FAFC] border border-[#E7ECF3] p-3 text-sm">
              <div className="font-semibold text-[#16324F]">{reassignTarget.to_location_name} · {reassignTarget.ref}</div>
              <div className="text-xs text-[#64748B]">Réceptionnaire actuel : {reassignTarget.receiver_name || "—"}</div>
            </div>
            <div>
              <label className="lbl">Nouveau réceptionnaire</label>
              <select className="inp" value={reassignReceiver} onChange={(e) => setReassignReceiver(e.target.value)} data-testid="reassign-receiver-select">
                <option value="">Sélectionner…</option>
                {receiverOpts.map((o) => <option key={o.id} value={o.id}>{o.name}{o.is_self ? " (moi)" : ` · ${roleLabel(o.role)}`}</option>)}
              </select>
              <p className="text-[11px] text-[#94A3B8] mt-1">La modification est tracée (ancien, nouveau, auteur, date/heure).</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
